import { useEffect, useId, useState } from 'react';
import { z } from 'zod';
import { Button } from '@/components/ui';
import { parseIsoDate } from '../store/syncJobsFiltersStore';

interface DateRangePickerProps {
  /** Currently applied range, ISO yyyy-MM-dd. */
  startDate: string;
  endDate: string;
  /** Fired when the user clicks Apply with a valid range. */
  onApply: (startDate: Date, endDate: Date) => void;
  /** Fired when the user clicks Reset. */
  onReset: () => void;
}

const rangeSchema = z
  .object({
    startDate: z.string().min(1, 'Start date is required'),
    endDate: z.string().min(1, 'End date is required'),
  })
  .refine((v) => v.endDate >= v.startDate, {
    message: 'End date must be on or after Start date',
    path: ['endDate'],
  });

/**
 * Replacement for `Modules/Shared/Templates/DateRangePicker.razor`.
 * Two native `<input type="date">` controls + Apply / Reset buttons.
 * Validation runs through Zod (`endDate >= startDate`).
 */
export function DateRangePicker({
  startDate,
  endDate,
  onApply,
  onReset,
}: DateRangePickerProps) {
  const [draftStart, setDraftStart] = useState(startDate);
  const [draftEnd, setDraftEnd] = useState(endDate);
  const [error, setError] = useState<string | null>(null);
  const startId = useId();
  const endId = useId();
  const errorId = useId();

  // Keep drafts in sync if the parent reapplies (e.g. Reset).
  useEffect(() => {
    setDraftStart(startDate);
    setDraftEnd(endDate);
    setError(null);
  }, [startDate, endDate]);

  function handleApply() {
    const parsed = rangeSchema.safeParse({ startDate: draftStart, endDate: draftEnd });
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? 'Invalid date range');
      return;
    }
    setError(null);
    onApply(parseIsoDate(parsed.data.startDate), parseIsoDate(parsed.data.endDate));
  }

  return (
    <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
      <div className="flex flex-col">
        <label htmlFor={startId} className="text-xs font-normal text-cool-gray-11">
          Start Date
        </label>
        <input
          id={startId}
          type="date"
          value={draftStart}
          onChange={(e) => setDraftStart(e.target.value)}
          className="block h-10 w-40 border border-cool-gray-2 px-3 text-sm focus:border-black focus:outline-none focus:ring-0"
          aria-invalid={!!error}
          aria-describedby={error ? errorId : undefined}
        />
      </div>
      <div className="flex flex-col">
        <label htmlFor={endId} className="text-xs font-normal text-cool-gray-11">
          End Date
        </label>
        <input
          id={endId}
          type="date"
          value={draftEnd}
          onChange={(e) => setDraftEnd(e.target.value)}
          className="block h-10 w-40 border border-cool-gray-2 px-3 text-sm focus:border-black focus:outline-none focus:ring-0"
          aria-invalid={!!error}
          aria-describedby={error ? errorId : undefined}
        />
      </div>
      <div className="flex items-end gap-2">
        <Button type="button" onClick={handleApply} disabled={draftEnd < draftStart}>
          Apply
        </Button>
        <Button type="button" variant="secondary" onClick={onReset}>
          Reset
        </Button>
      </div>
      {error ? (
        <p id={errorId} role="alert" className="text-sm text-red-600 sm:ml-3">
          {error}
        </p>
      ) : null}
    </div>
  );
}
